import { Injectable } from '@angular/core';

import { IMarker as _IMarker, MapMarkerIcon, IMapMarkerStyleOptions, IMapMarkerOptions } from '@lib/geo/map';

import { IEsriMarker } from '../interfaces';
import { EsriMap } from '../../../map';

type IMarker = _IMarker<EsriMap>;

/**
 * Service responsible for style operations on ESRI Map markers.
 * Handles visual appearance, icons, colors, and other styling properties.
 * Note: Badge-related functionality is handled by EsriMarkerBadgeService.
 */
@Injectable()
export class EsriMarkerStyleService {
  public setStyle(esriMarker: IEsriMarker, marker: IMarker, style: IMapMarkerStyleOptions): void {
    if (style.rotation !== undefined) {
      esriMarker.setRotation(style.rotation);
    }

    // Filter out deprecated properties that are no longer supported
    const { border, shape, ...validStyle } = style;
    marker.styles = { ...marker.styles, ...validStyle };
  }

  public setOptions(esriMarker: IEsriMarker, marker: IMarker, options: Partial<IMapMarkerOptions>): void {
    if (options.draggable !== undefined) {
      this.setDraggable(esriMarker, marker, options.draggable);
    }
    if (options.rotation !== undefined) {
      this.setRotation(esriMarker, marker, options.rotation);
    }
    if (options.title !== undefined) {
      this.setTitle(esriMarker, marker, options.title);
    }
    if (options.zIndex !== undefined) {
      this.setZIndex(esriMarker, marker, options.zIndex);
    }
  }

  public setDraggable(esriMarker: IEsriMarker, marker: IMarker, draggable: boolean): void {
    esriMarker.setDraggable(draggable);
    marker.draggable = draggable;
  }

  public setRotation(esriMarker: IEsriMarker, marker: IMarker, rotation: number): void {
    esriMarker.setRotation(rotation);
    marker.rotation = rotation;
  }

  public setZIndex(esriMarker: IEsriMarker, marker: IMarker, zIndex: number): void {
    esriMarker.setZIndex(zIndex);
    marker.zIndex = zIndex;
  }

  public setTitle(esriMarker: IEsriMarker, marker: IMarker, title: string): void {
    esriMarker.setTitle(title);
    marker.title = title;
  }

  public getStyle(marker: IMarker): IMapMarkerStyleOptions {
    return marker.styles || {};
  }

  public getIcon(marker: IMarker): MapMarkerIcon | undefined {
    return marker.icon;
  }

  public getZIndex(esriMarker: IEsriMarker): number {
    return esriMarker.getZIndex();
  }

  public getTitle(esriMarker: IEsriMarker): string {
    return esriMarker.getTitle();
  }

  public getDraggable(esriMarker: IEsriMarker): boolean {
    return esriMarker.getDraggable();
  }

  public getRotation(esriMarker: IEsriMarker): number {
    return esriMarker.getRotation();
  }
}
